export interface AsistanUserEmail {
  email: string
  name: string | null
  created_at: number
}

export interface AsistanAssistantPerUser {
  email: string
  assistants: number
}

export interface AsistanLargestContext {
  name: string | null
  email: string | null
  content_chars: number
  data_chars: number
  total_chars: number
}

export interface AsistanUsagePerUser {
  email: string
  name: string | null
  used_seconds: number
  used_minutes: number
}

export interface AsistanCall {
  id: number
  caller_phone: string | null
  started_at: number
  assistant: string | null
  email: string | null
}

export interface AsistanGateway {
  registered: number | null
  last_beat: number | null
  last_call_at: number | null
}

export interface AsistanContent {
  notes: number
  transcripts: number
  appointments: number
}

export interface AsistanData {
  users: {
    registered: number
    active: number
    emails: AsistanUserEmail[]
  }
  assistants: {
    total: number
    active: number
    perUser: AsistanAssistantPerUser[]
  }
  largestContexts: AsistanLargestContext[]
  usage: {
    perUser: AsistanUsagePerUser[]
    totalSeconds: number
    totalMinutes: number
  }
  calls: {
    active: number
    list: AsistanCall[]
  }
  health: {
    gateway: AsistanGateway | null
    content: AsistanContent | null
  }
}

// All `*_at` columns are epoch milliseconds.
export function formatEpochMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—'
  const date = new Date(ms)
  if (isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

// `used_seconds` is seconds; minutes = used_seconds / 60.
export function formatMinutes(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 })
}

/* ------------------------------------------------------------------ insights */

// The shapes below mirror functions/api/project/asistan-insights.ts. Money is
// reported as `*Minor` (cents/kuruş) plus its currency, exactly as stored, so the UI
// never has to guess which currency an amount is in.

export type InsightsTab = 'overview' | 'users' | 'calls' | 'expenses'

export interface InsightsConfig {
  geminiUsdPerMinute: number
  tryPerUsd: number
  trialSeconds: number
}

export interface InsightsFiltersEcho {
  days: number | null
  from: number | null
  to: number | null
  email: string
  account_type: string | null
  plan: string | null
  activity: string | null
  mode: string | null
  status: string | null
  assistant: string | null
  deleted: 'active' | 'all' | 'deleted'
  limit: number
}

export interface InsightsSummary {
  users: number
  callers: number
  businessUsers: number
  assistants: number
  activeAssistants: number
  liveCalls: number
  calls: number
  callSeconds: number
  callMinutes: number
  billedSeconds: number
  billedMinutes: number
  remainingSeconds: number
  smsCredits: number
  costUsd: number
  costTry: number
  revenueUsdMinor: number
  revenueTryMinor: number
}

export interface InsightsUserRow {
  id: string
  email: string
  name: string | null
  account_type: string
  business_type: string
  language: string
  created_at: number
  used_seconds: number
  balance_seconds: number
  sms_balance: number
  deleted_at: number | null
  assistants: number
  active_assistants: number
  live_calls: number
  call_seconds: number
  answered_calls: number
  missed_calls: number
  revenue_usd_minor: number
  revenue_try_minor: number
  last_payment_at: number | null
  used_minutes: number
  call_minutes: number
  cost_usd: number
}

export interface InsightsCallRow {
  id: string
  user_id: string
  email: string
  name: string | null
  account_type: string
  assistant: string
  caller_phone: string | null
  mode: string
  status: string
  duration_sec: number
  started_at: number
  ended_at: number | null
}

export interface InsightsPayerRow {
  id: string
  email: string
  name: string | null
  revenue_usd_minor: number
  revenue_try_minor: number
  minutes_purchased: number
  sms_purchased: number
  payments: number
  last_paid_at: number | null
}

export interface InsightsPackRow {
  pack_id: string
  currency: string
  payments: number
  minutes: number
  sms_count: number
  amount_minor: number
  last_paid_at: number
}

export interface InsightsContextRow {
  name: string | null
  email: string | null
  active: number
  content_chars: number
  data_chars: number
  total_chars: number
}

export interface InsightsSeriesRow {
  day: string
  users: number
  billed_seconds: number
  calls: number
  revenue_usd_minor: number
  revenue_try_minor: number
}

export interface InsightsOptions {
  accountTypes: { value: string; count: number }[]
  // Capped at the busiest 100; `assistantsTruncated` says whether anything was cut.
  assistants: { value: string; count: number }[]
  modes: { value: string; count: number }[]
  assistantsTruncated: boolean
}

export interface InsightsData {
  generatedAt: number
  tab: InsightsTab
  config: InsightsConfig
  // 'lifetime' means the tab ignores the date window on purpose (see the endpoint).
  scope: 'filtered' | 'lifetime'
  filters: InsightsFiltersEcho
  summary: InsightsSummary
  users?: InsightsUserRow[]
  calls?: InsightsCallRow[]
  payers?: InsightsPayerRow[]
  packs?: InsightsPackRow[]
  contexts?: InsightsContextRow[]
  series?: InsightsSeriesRow[]
  options?: InsightsOptions
  truncated?: boolean
  truncatedPayers?: boolean
}

// The filter set as the UI holds it — strings, because every control is an input.
export interface Filters {
  days: string
  from: string
  to: string
  email: string
  account_type: string
  plan: string
  activity: string
  mode: string
  status: string
  assistant: string
  deleted: string
  limit: string
}

export const EMPTY_FILTERS: Filters = {
  days: '30',
  from: '',
  to: '',
  email: '',
  account_type: '',
  plan: '',
  activity: '',
  mode: '',
  status: '',
  assistant: '',
  deleted: 'active',
  limit: '500',
}

export const RANGE_PRESETS = [
  { value: '', label: 'All time' },
  { value: '1', label: 'Today' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
]

export const PLAN_OPTIONS = [
  { value: '', label: 'Any account state' },
  { value: 'paid', label: 'Paid — has bought minutes' },
  { value: 'trial', label: 'Trial — calling on the free grant' },
  { value: 'exhausted', label: 'Exhausted — ran out, never paid' },
  { value: 'never', label: 'Never called — signed up only' },
]

export const ACTIVITY_OPTIONS = [
  { value: '', label: 'Any activity' },
  { value: 'active', label: 'Has billed minutes' },
  { value: 'dormant', label: 'No billed minutes' },
]

export const DELETED_OPTIONS = [
  { value: 'active', label: 'Active accounts only' },
  { value: 'all', label: 'Include scheduled for deletion' },
  { value: 'deleted', label: 'Deletion grace period only' },
]

export const MODE_OPTIONS = [
  { value: '', label: 'Any mode' },
  { value: 'ai', label: 'AI answered' },
  { value: 'human', label: 'Rang the owner' },
]

export const STATUS_OPTIONS = [
  { value: '', label: 'Any outcome' },
  { value: 'answered', label: 'Answered' },
  { value: 'missed', label: 'Missed' },
]

// Which controls each tab actually shows. Keeping this in one table is what stops
// the two call-only filters from lingering in the users view where they do nothing.
export const TAB_FILTERS: Record<InsightsTab, (keyof Filters)[]> = {
  overview: [],
  users: ['days', 'from', 'to', 'email', 'account_type', 'plan', 'activity', 'deleted', 'limit'],
  calls: ['mode', 'status', 'assistant', 'email', 'days', 'from', 'to', 'account_type', 'deleted', 'limit'],
  expenses: [],
}

// Only these keys travel in the query string; anything else in the URL is ignored.
export const FILTER_KEYS = Object.keys(EMPTY_FILTERS) as (keyof Filters)[]

export function filtersToQuery(filters: Filters, tab: InsightsTab): string {
  const params = new URLSearchParams()
  params.set('tab', tab)
  // A custom from/to replaces the range preset; `days` is not written alongside it,
  // because the endpoint prefers `days` and a shared link would then quietly ignore
  // the dates that were the whole point of the link. `days` must also not be written
  // when it is blank (the "choosing a custom range" state) for the same reason: the
  // default would reappear on the way back and re-scope the view.
  const customRange = filters.from !== '' || filters.to !== ''
  for (const key of FILTER_KEYS) {
    if (key === 'days' && customRange) continue
    const value = filters[key]
    if (value && value !== EMPTY_FILTERS[key]) params.set(key, value)
  }
  return params.toString()
}

export function filtersFromQuery(search: string): Filters {
  const params = new URLSearchParams(search)
  const filters = { ...EMPTY_FILTERS }
  for (const key of FILTER_KEYS) {
    const value = params.get(key)
    if (value !== null) filters[key] = value
  }
  return filters
}

/* ------------------------------------------------------------------ formatting */

export function formatMinor(minor: number, currency: string): string {
  const amount = (minor ?? 0) / 100
  if (currency.toLowerCase() === 'try') {
    return `₺${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function formatUsd(value: number): string {
  return `$${(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function formatTry(value: number): string {
  return `₺${(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function formatSecondsShort(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.round(seconds ?? 0))
  const minutes = Math.floor(total / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

/**
 * Exact duration, always including seconds. Used where a floor-to-minutes
 * display would hide the real value (e.g. a 25s call rendering as "0m").
 */
export function formatDuration(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.round(seconds ?? 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  if (hours > 0) return `${hours}h ${minutes}m ${secs}s`
  if (minutes > 0) return `${minutes}m ${secs}s`
  return `${secs}s`
}

export function formatDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return day
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
}

export function formatMonth(day: string): string {
  const [y, m] = day.split('-').map(Number)
  if (!y || !m) return day
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
}

// The API reports seconds; whatever an operator reads should be minutes, because
// minutes are the unit the product is sold in.
export function secondsToMinutes(seconds: number | null | undefined): number {
  return Math.round(((seconds ?? 0) / 60) * 10) / 10
}

